import React from "react";
import { Avatar, Box, CircularProgress, Divider, Paper, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import patternUrl from "../../../../assets/pattern.png";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";

interface RolePreviewProps {
  roleName: string;
  selectedCount: number;
  totalCount: number;
  currentStep: number;
}

const RolePreview: React.FC<RolePreviewProps> = ({
  roleName,
  selectedCount,
  totalCount,
  currentStep,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const percent = totalCount > 0 ? Math.round((selectedCount / totalCount) * 100) : 0;

  return (
    <Paper
      sx={{
        p: 0,
        borderRadius: 3,
        overflow: "hidden",
        height: "100%",
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
      }}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          minHeight: 120,
          p: 3,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          backgroundImage: `linear-gradient(135deg, ${reduxTheme.primary_colors?.[400]} 0%, ${reduxTheme.primary_colors?.[10]} 50%, ${reduxTheme.primary_colors?.[400]} 100%), url(${patternUrl})`,
          backgroundSize: "cover, 250%",
          backgroundPosition: "center, center",
          backgroundBlendMode: "multiply",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "#fff",
                width: 40,
                height: 40,
                borderRadius: 2,
              }}
            >
              <ShieldOutlinedIcon />
            </Avatar>
            <Box>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
                Role preview
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: "#fff" }} noWrap>
                {roleName.trim() || "Untitled Role"}
              </Typography>
            </Box>
          </Box>

          {currentStep >= 1 && (
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              <CircularProgress
                variant="determinate"
                value={percent}
                size={52}
                thickness={4}
                sx={{ color: "#fff", backgroundColor: "rgba(255,255,255,0.2)", borderRadius: "50%" }}
              />
              <Box
                sx={{
                  inset: 0,
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="caption" fontWeight={700} sx={{ color: "#fff" }}>
                  {percent}%
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Divider />

      <Box sx={{ flexGrow: 1, overflow: "auto", px: 3, pb: 3, pt: 2.5 }}>
        {currentStep === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Give the role a name, then move on to choosing which permissions it
            grants.
          </Typography>
        ) : (
          <>
            <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>
              Permissions granted
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              This role will have {selectedCount} of {totalCount} available
              permissions.
            </Typography>

            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Selected
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {selectedCount}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Available
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {totalCount}
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default RolePreview;
